import {
  Alert,
  Button,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row
} from 'reactstrap';
import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import enCountries from '../../../i18n/static-messages/en-countries.json';
import fiCountries from '../../../i18n/static-messages/fi-countries.json';
import svCountries from '../../../i18n/static-messages/sv-countries.json';
import LocalizationStore from '../../../store/LocalizationStore';
import { modelOf } from '../../../prop-types';
import {
  domainMatchesUrl,
  transformDomainToUrl,
  transformExternalLink
} from '../../../util/url';
import IntlWrapper from '../../../i18n/IntlWrapper';
import LanguageStore from '../../../store/LanguageStore';
import TranslationStore from '../../../store/TranslationStore';
import { allowScroll, blockScroll } from '../../../util/dom';
import { browserLanguage } from '../../../util/browser';

const SITE_CHOSEN_PARAMETER = '?siteChosen=1';

@observer
export class RedirectModal extends Component {
  translations = null;

  constructor(props) {
    super(props);

    if (this.siteSuggestionEnabled()) {
      props.localizationStore.localizationModal.toggle();
      blockScroll();

      this.translations = {
        en: enCountries,
        fi: fiCountries,
        sv: svCountries
      };
    }
  }

  componentDidUpdate() {
    if (this.siteSuggestionEnabled()) {
      allowScroll();
    }
  }

  componentWillUnmount() {
    allowScroll();
  }

  siteSuggestionEnabled = () => {
    const { localizationStore } = this.props;

    // Enabled when localStorage has no key set and backend has sent data.
    return (
      !localizationStore.getLocalStorage() &&
      localizationStore.suggestionEnabled
    );
  };

  hasShipping = () => {
    const { sites } = this.props.localizationStore;
    let hasShipping = false;
    sites.forEach((site) => {
      if (site.shipsToCountry === true) {
        hasShipping = true;
      }
    });
    return hasShipping;
  };

  handleSuggestionIgnore = () => {
    const { localizationStore } = this.props;

    localizationStore.setLocalStorage();
    localizationStore.localizationModal.toggle();
    allowScroll();
  };

  redirect = (url) => {
    this.handleSuggestionIgnore();
    window.location.assign(transformExternalLink(url + SITE_CHOSEN_PARAMETER));
  };

  getSites = () => {
    const { sites } = this.props.localizationStore;
    const currentSite = window && window.location.origin + '/';

    if (sites.length === 0) {
      return null;
    }

    return sites.map((site) => {
      const isCurrent = domainMatchesUrl(site.domain, currentSite);

      return (
        <Alert
          key={site.domain}
          className={classNames('RedirectModal__frame', {
            'RedirectModal__frame-suggested': site.recommendedForCountry
          })}
        >
          <Row key={site.domain}>
            <Col key={site.domain} className="RedirectModal__site" xs={12}>
              <div className="RedirectModal__site-name">{site.domain}</div>
            </Col>
            <Col xs={12} sm={7}>
              {site.recommendedForCountry && (
                <div className="RedirectModal__site-suggested">
                  <FormattedMessage
                    id="localization.isSuggestedSite"
                    defaultMessage="We recommend using this online store."
                  />
                </div>
              )}
              {site.shipsToCountry && (
                <div className="RedirectModal__site-shippingPossible">
                  <FormattedMessage
                    id="localization.shippingPossible"
                    defaultMessage="Shipping is possible to your country."
                  />
                </div>
              )}
            </Col>
            <Col xs={12} sm={5} className="RedirectModal__button">
              <div className="RedirectModal__button-div">
                <Button
                  color={site.recommendedForCountry ? 'primary' : 'secondary'}
                  block
                  className="RedirectModal__button--accept"
                  role="link"
                  onClick={() =>
                    this.redirect(transformDomainToUrl(site.domain))
                  }
                >
                  <div className="RedirectModal__accept-text">
                    {isCurrent ? (
                      <FormattedMessage
                        id="localization.doNotRedirect"
                        defaultMessage="Stay on this online store"
                      />
                    ) : (
                      <FormattedMessage
                        id="localization.moveToSite"
                        defaultMessage="Go to the online store"
                      />
                    )}
                  </div>
                </Button>
              </div>
            </Col>
          </Row>
        </Alert>
      );
    });
  };

  render() {
    const { localizationStore, languageStore, translationStore } = this.props;

    if (!this.siteSuggestionEnabled()) {
      return null;
    }

    const modal = localizationStore.localizationModal;
    const sites = this.getSites();

    const navigatorLanguage = browserLanguage();
    const overrideLocale = navigatorLanguage && navigatorLanguage.language;

    return (
      <IntlWrapper
        languageStore={languageStore}
        translationStore={translationStore}
        loadableMessages={this.translations}
        overrideLocale={overrideLocale}
      >
        <Modal
          className="RedirectModal"
          size="md"
          isOpen={modal.isOpen}
          toggle={this.handleSuggestionIgnore}
          backdrop="static"
          keyboard={false}
        >
          <ModalHeader toggle={this.handleSuggestionIgnore}>
            {localizationStore.countryCode && (
              <Row className="RedirectModal__localization-header">
                <Col xs={12} className="RedirectModal__localization-phrase">
                  <div className="RedirectModal__localization-phrase-country">
                    <FormattedMessage
                      id="localization.yourLocation"
                      defaultMessage="We detected you are in {country}"
                      values={{
                        country: (
                          <FormattedMessage
                            id={localizationStore.countryCode}
                          />
                        )
                      }}
                    />
                  </div>
                </Col>
              </Row>
            )}
            {!localizationStore.countryCode && (
              <FormattedMessage
                id="localization.noSuggestion"
                defaultMessage="We do not recognize where you are. Please check out our available online stores."
              />
            )}
          </ModalHeader>
          {!!sites && (
            <ModalBody>
              <div className="RedirectModal__body">
                {!this.hasShipping() &&
                  !!localizationStore.countryCode && (
                    <div className="RedirectModal__noShipping">
                      <FormattedMessage
                        id="localization.noShipping"
                        defaultMessage="Please check out our available online stores as unfortunately we do not deliver to the detected country."
                      />
                    </div>
                  )}
                <div className="RedirectModal__sites">
                  <div className="RedirectModal__sites-row">{sites}</div>
                </div>
              </div>
            </ModalBody>
          )}
        </Modal>
      </IntlWrapper>
    );
  }
}

RedirectModal.propTypes = {
  LocalizationStore: modelOf(LocalizationStore),
  languageStore: modelOf(LanguageStore),
  translationStore: modelOf(TranslationStore)
};

export default inject('localizationStore', 'languageStore', 'translationStore')(
  RedirectModal
);



// WEBPACK FOOTER //
// ./src/components/localization/RedirectModal/index.jsx